import React, { useCallback, useContext, useState } from 'react';

import { connect } from 'react-redux';
import {
  Field,
  FieldArray,
  propTypes as reduxFormPropTypes,
  reduxForm,
  SubmissionError,
} from 'redux-form';
import withStyles from 'isomorphic-style-loader/withStyles';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import _ from 'lodash';

import {
  FormattedHTMLMessage,
  FormattedMessage,
  injectIntl,
  intlShape,
} from 'react-intl';
import {
  ModalCloseBtn,
  ModalSaveBtn,
} from '../statelessComponents/Buttons/ButtonsInModals';

import s from './AddVismaSignModal.scss';
import messages from './messages';
import TextField from '../statelessComponents/FormFields/TextField';
import ContextProvider from '../../context';
import addVismaSignValidation from './addVismaSignValidation';
import ConfirmDeleteDialog from '../statelessComponents/ConfirmDeleteDialog';
import MultipleSignersFormField from './MultipleSignersFormField/MultipleSignersFormField';

const AddVismaSignModal = props => {
  const {
    intl: { formatMessage },
    form,
    handleSubmit,
    submitting,
    onClose,
    onSubmit,
    submitRoute,
    invalid,
    touch,
    isMultipleSigners,
    maxSignersAmount,
    customMessages: {
      modalSaveBtnText,
      addVismaSignBtnLabel,
      confirmTitle,
      getInvitationsToSignWillBeSentTo,
    },
  } = props;
  const { fetch } = useContext(ContextProvider);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);

  const hideConfirmSave = useCallback(() => {
    setIsConfirmSaveOpen(false);
  }, [isConfirmSaveOpen, setIsConfirmSaveOpen]);

  const submit = async data => {
    setIsSubmitted(false);

    return fetch(submitRoute, {
      method: 'POST',
      body: JSON.stringify(data),
    })
      .catch(err => {
        throw new SubmissionError({ _error: err && err.message });
      })
      .then(async res => {
        if (!res.ok) {
          const resJson = await res.json();
          if (resJson.message) {
            throw new SubmissionError({ _error: resJson.message });
          }
          throw new SubmissionError(resJson);
        }
        setIsSubmitted(true);
        onSubmit();
        onClose();
      });
  };

  const handleConfirmationAccepted = event => {
    event.preventDefault();
    hideConfirmSave();
    return handleSubmit(submit)(event);
  };

  const showConfirmSave = event => {
    event.preventDefault();
    const { values, syncErrors } = form;
    if (invalid) {
      return touch(...Object.keys(syncErrors || values));
    }

    return setIsConfirmSaveOpen(true);
  };

  return (
    <div>
      <Typography variant="h5" component="h1" id="add-visma-sign-modal-title">
        <FormattedMessage {...messages.modalTitle} />
      </Typography>
      <form noValidate autoComplete="off">
        {!isMultipleSigners && (
          <Field
            name="signerEmail"
            type="email"
            label={formatMessage(messages.signerEmail)}
            margin="normal"
            component={TextField}
            fullWidth
            disabled={submitting}
            className={s.signerEmail}
          />
        )}
        {isMultipleSigners && (
          <FieldArray
            name="signerEmails"
            component={MultipleSignersFormField}
            label={formatMessage(messages.signerEmail)}
            fullWidth
            disabled={submitting}
            maxSignersAmount={maxSignersAmount}
          />
        )}
        <ModalSaveBtn
          data-test-id="Cr8aoWHo4ntr-VUDAivML"
          isLoaderOn={submitting}
          onClick={showConfirmSave}
          isSuccessed={isSubmitted}
          mobileLandscapeRelative
          disabled={submitting}
          tooltip={addVismaSignBtnLabel || formatMessage(messages.addVismaSign)}
          id="add-visma-sign-modal-generate-bundle-btn"
        />
      </form>
      <ModalCloseBtn
        data-test-id="nj1rejk78cw4qPmMe1b3r"
        fitMobileLandscapeFullWidth
        id="add-visma-sign-modal-close-btn"
        onClick={onClose}
      />
      <ConfirmDeleteDialog
        isShown={isConfirmSaveOpen}
        onClose={hideConfirmSave}
        submit={handleConfirmationAccepted}
        isDeleting={submitting}
        deleteBtnId="confirm-add-visma-sign"
        submitColor="primary"
        deleteBtnLabel={modalSaveBtnText || formatMessage(messages.saveLabel)}
        title={confirmTitle || <FormattedMessage {...messages.confirmTitle} />}
        description={
          !getInvitationsToSignWillBeSentTo ? (
            <FormattedHTMLMessage
              {...messages.invitatationToSignWillBeSentTo}
              values={{
                signerEmail: _.get(form, 'values.signerEmail', ''),
              }}
            />
          ) : (
            getInvitationsToSignWillBeSentTo(
              _.get(form, 'values.signerEmails', '').join(', '),
            )
          )
        }
      />
    </div>
  );
};

AddVismaSignModal.propTypes = {
  intl: intlShape.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  customMessages: PropTypes.shape({
    modalSaveBtnText: PropTypes.string,
    addVismaSignBtnLabel: PropTypes.string,
    confirmTitle: PropTypes.string,
    getInvitationsToSignWillBeSentTo: PropTypes.func,
  }),
  ...reduxFormPropTypes,
};

AddVismaSignModal.defaultProps = { customMessages: {} };
export default compose(
  injectIntl,
  withStyles(s),
  connect((state, props) => {
    const initialValues = {};

    if (props.isMultipleSigners) {
      initialValues.signerEmails = [''];
    }

    return {
      initialValues,
    };
  }),
  reduxForm({
    form: 'addVismaSign',
    validate: addVismaSignValidation,
  }),
  connect(state => ({
    form: state.form.addVismaSign,
  })),
)(AddVismaSignModal);
